import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getJobSeekerProfiles as getFirebaseProfiles } from '../lib/firebase/jobSeekers';
import { getJobSeekerProfiles as getSupabaseProfiles } from '../lib/supabase/jobSeekers';
import type { JobSeekerProfile } from '../types/profiles';

export function useJobSeekerProfiles() {
  const { user, firebaseUser } = useAuth();
  const [profiles, setProfiles] = useState<JobSeekerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [connectionError, setConnectionError] = useState(false);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      setConnectionError(false);
      
      // Load profiles from both Firebase (existing) and Supabase (new paid)
      const [firebaseResult, supabaseResult] = await Promise.all([
        getFirebaseProfiles(),
        getSupabaseProfiles()
      ]);
      
      if (firebaseResult.error) {
        console.warn('Error loading Firebase profiles:', firebaseResult.error);
        setConnectionError(true);
      }
      
      if (supabaseResult.error) {
        console.warn('Error loading Supabase profiles:', supabaseResult.error);
      }
      
      // Combine profiles from both sources
      const allProfiles = [
        ...supabaseResult.profiles, // Put Supabase profiles first (newer)
        ...firebaseResult.profiles
      ];
      
      setProfiles(allProfiles);
    } catch (err) {
      console.error('Error loading profiles:', err);
      setError(err as Error);
      setConnectionError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  return { 
    profiles, 
    loading, 
    error,
    connectionError,
    refreshProfiles: loadProfiles
  };
}